import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Box, IconButton, Skeleton, Typography, useTheme } from '@mui/material';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import DownloadIcon from '@mui/icons-material/Download';
import PlayCircleOutlineIcon from '@mui/icons-material/PlayCircleOutline';
import PhotoCameraOutlinedIcon from '@mui/icons-material/PhotoCameraOutlined';
import { getAuthHeaders } from '../../utils/auth';
import { albumVideoImagePosterUrl, albumVideoSrcWithPeekHint } from '../../utils/albumVideoThumbnail';

const ROW_HEIGHT = 220;
/** Photos per request (horizontal strip: first batch + each load-more) */
const PAGE_SIZE = 5;
const SKELETON_COUNT = 3;
const LONG_PRESS_MS = 500;

export interface FeedAlbumPhoto {
   id: string;
   url?: string;
   thumbnailUrl?: string;
   title?: string;
   mediaType?: string;
   videoUrl?: string;
}

interface PhotosPageResponse {
   photos: FeedAlbumPhoto[];
   totalCount: number;
   nextCursor: string | null;
}

function sanitizeFileBase(name: string): string {
   return name.replace(/[^a-zA-Z0-9._-]+/g, '_').replace(/^_|_$/g, '') || 'album';
}

const VIDEO_URL_EXT = /\.(mp4|webm|mov|m4v|ogv|avi|mkv)(\?|#|$)/i;

/** Legacy Firestore docs may omit mediaType; infer from URL. */
function feedPhotoLooksLikeVideo(photo: FeedAlbumPhoto): boolean {
   const u = (photo.videoUrl || photo.url || '').trim();
   return Boolean(u && VIDEO_URL_EXT.test(u));
}

const InlinePhotoScroller: React.FC<{
   albumId: string;
   albumTitle: string;
   /** Shown until API returns totalCount */
   initialPhotoCount?: number;
   /** Increment after uploads (or other server-side changes) to refetch the strip without changing albumId */
   refreshKey?: number;
}> = ({ albumId, albumTitle, initialPhotoCount = 0, refreshKey = 0 }) => {
   const theme = useTheme();
   const scrollRef = useRef<HTMLDivElement>(null);
   const sentinelRef = useRef<HTMLDivElement>(null);
   const longPressTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
   const nextCursorRef = useRef<string | null>(null);
   const loadingMoreRef = useRef(false);
   const dragRef = useRef({ active: false, startX: 0, scrollLeft: 0 });

   const [photos, setPhotos] = useState<FeedAlbumPhoto[]>([]);
   const [totalCount, setTotalCount] = useState(initialPhotoCount);
   const [nextCursor, setNextCursor] = useState<string | null>(null);
   const [loadingInitial, setLoadingInitial] = useState(true);
   const [loadingMore, setLoadingMore] = useState(false);
   const [fetchError, setFetchError] = useState<string | null>(null);
   const [visibleEndIndex, setVisibleEndIndex] = useState(0);
   const [hoverStrip, setHoverStrip] = useState(false);
   const [loadedIds, setLoadedIds] = useState<Set<string>>(new Set());
   const [overlayPhotoId, setOverlayPhotoId] = useState<string | null>(null);
   const [downloadingId, setDownloadingId] = useState<string | null>(null);
   const [playingVideoId, setPlayingVideoId] = useState<string | null>(null);
   const videoRef = useRef<HTMLVideoElement | null>(null);

   const base = process.env.REACT_APP_API_URL || '';

   const loadPage = useCallback(
      async (cursor: string | null, append: boolean) => {
         const headers = await getAuthHeaders();
         const params = new URLSearchParams({ limit: String(PAGE_SIZE) });
         if (cursor) params.set('cursor', cursor);
         const res = await fetch(`${base}/api/albums/${albumId}/photos?${params.toString()}`, {
            headers,
         });
         const data = (await res.json().catch(() => ({}))) as PhotosPageResponse & { message?: string };
         if (!res.ok) {
            throw new Error(data.message || 'Failed to load photos');
         }
         setTotalCount(data.totalCount ?? 0);
         const nc = data.nextCursor ?? null;
         setNextCursor(nc);
         nextCursorRef.current = nc;
         setPhotos((prev) => {
            if (!append) return data.photos || [];
            const seen = new Set(prev.map((p) => p.id));
            const merged = [...prev];
            for (const p of data.photos || []) {
               if (!seen.has(p.id)) {
                  seen.add(p.id);
                  merged.push(p);
               }
            }
            return merged;
         });
      },
      [albumId, base]
   );

   useEffect(() => {
      let cancelled = false;
      (async () => {
         nextCursorRef.current = null;
         setLoadingInitial(true);
         setFetchError(null);
         setPhotos([]);
         setNextCursor(null);
         nextCursorRef.current = null;
         try {
            await loadPage(null, false);
         } catch (e) {
            if (!cancelled) {
               setFetchError(e instanceof Error ? e.message : 'Failed to load photos');
            }
         } finally {
            if (!cancelled) setLoadingInitial(false);
         }
      })();
      return () => {
         cancelled = true;
      };
   }, [albumId, loadPage, refreshKey]);

   useEffect(() => {
      const el = scrollRef.current;
      if (!el) return;
      const onWheel = (e: WheelEvent) => {
         if (Math.abs(e.deltaY) <= Math.abs(e.deltaX)) return;
         e.preventDefault();
         el.scrollLeft += e.deltaY;
      };
      el.addEventListener('wheel', onWheel, { passive: false });
      return () => el.removeEventListener('wheel', onWheel);
   }, [loadingInitial]);

   useEffect(() => {
      const onMove = (e: MouseEvent) => {
         if (!dragRef.current.active || !scrollRef.current) return;
         e.preventDefault();
         const dx = e.pageX - dragRef.current.startX;
         scrollRef.current.scrollLeft = dragRef.current.scrollLeft - dx;
      };
      const onUp = () => {
         dragRef.current.active = false;
      };
      window.addEventListener('mousemove', onMove);
      window.addEventListener('mouseup', onUp);
      return () => {
         window.removeEventListener('mousemove', onMove);
         window.removeEventListener('mouseup', onUp);
      };
   }, []);

   const updateVisibleIndex = useCallback(() => {
      const container = scrollRef.current;
      if (!container || photos.length === 0) {
         setVisibleEndIndex(0);
         return;
      }
      const cRect = container.getBoundingClientRect();
      let maxIdx = 0;
      const children = container.querySelectorAll('[data-photo-tile="1"]');
      children.forEach((node, i) => {
         const r = node.getBoundingClientRect();
         if (r.left < cRect.right && r.right > cRect.left) {
            maxIdx = Math.max(maxIdx, i + 1);
         }
      });
      setVisibleEndIndex(maxIdx);
   }, [photos.length]);

   useEffect(() => {
      const el = scrollRef.current;
      if (!el) return;
      updateVisibleIndex();
      el.addEventListener('scroll', updateVisibleIndex, { passive: true });
      return () => el.removeEventListener('scroll', updateVisibleIndex);
   }, [photos, updateVisibleIndex]);

   useEffect(() => {
      nextCursorRef.current = nextCursor;
   }, [nextCursor]);

   useEffect(() => {
      const sent = sentinelRef.current;
      const container = scrollRef.current;
      if (!sent || !container || loadingInitial) return;

      const obs = new IntersectionObserver(
         (entries) => {
            const hit = entries.some((e) => e.isIntersecting);
            if (!hit || loadingMoreRef.current) return;
            const cursor = nextCursorRef.current;
            if (!cursor) return;
            loadingMoreRef.current = true;
            setLoadingMore(true);
            loadPage(cursor, true)
               .catch((e) => setFetchError(e instanceof Error ? e.message : 'Load failed'))
               .finally(() => {
                  loadingMoreRef.current = false;
                  setLoadingMore(false);
               });
         },
         { root: container, rootMargin: '160px', threshold: 0 }
      );
      obs.observe(sent);
      return () => obs.disconnect();
   }, [loadingInitial, loadPage, nextCursor]);

   // Pause previous video when switching or unmounting
   useEffect(() => {
      return () => {
         if (videoRef.current) {
            videoRef.current.pause();
         }
      };
   }, [playingVideoId]);

   const scrollByAmount = (dir: -1 | 1) => {
      const el = scrollRef.current;
      if (!el) return;
      el.scrollBy({ left: dir * Math.min(400, el.clientWidth * 0.85), behavior: 'smooth' });
   };

   const handleDownload = async (photo: FeedAlbumPhoto, indexOneBased: number) => {
      if (downloadingId) return;
      setDownloadingId(photo.id);
      try {
         const headers = await getAuthHeaders();
         const res = await fetch(`${base}/api/albums/${albumId}/photos/${photo.id}/download`, {
            headers,
         });
         if (!res.ok) {
            const err = await res.json().catch(() => ({}));
            throw new Error((err as { message?: string }).message || 'Download failed');
         }
         const blob = await res.blob();
         const ext =
            photo.mediaType === 'video' || blob.type.startsWith('video/')
               ? 'mp4'
               : blob.type.includes('png')
                 ? 'png'
                 : 'jpg';
         const fname = `${sanitizeFileBase(albumTitle)}_${indexOneBased}.${ext}`;
         const url = URL.createObjectURL(blob);
         const a = document.createElement('a');
         a.href = url;
         a.download = fname;
         a.style.display = 'none';
         document.body.appendChild(a);
         a.click();
         document.body.removeChild(a);
         URL.revokeObjectURL(url);
      } catch (e) {
         console.error(e);
      } finally {
         setDownloadingId(null);
      }
   };

   const startLongPress = (photoId: string) => {
      longPressTimer.current = setTimeout(() => {
         setOverlayPhotoId(photoId);
      }, LONG_PRESS_MS);
   };
   const endLongPress = () => {
      if (longPressTimer.current) {
         clearTimeout(longPressTimer.current);
         longPressTimer.current = null;
      }
   };

   const counterLabel =
      totalCount === 0
         ? '0 of 0'
         : visibleEndIndex > 0
           ? `${visibleEndIndex} of ${totalCount}`
           : `${Math.min(photos.length, totalCount)} of ${totalCount}`;

   if (fetchError && photos.length === 0 && !loadingInitial) {
      return (
         <Box sx={{ mb: 1.5 }}>
            <Typography variant="caption" color="error">
               {fetchError}
            </Typography>
         </Box>
      );
   }

   if (!loadingInitial && totalCount === 0) {
      return (
         <Box sx={{ mb: 1.5 }}>
            <Typography
               variant="caption"
               sx={{
                  fontWeight: 600,
                  color: theme.palette.text.secondary,
                  display: 'block',
                  mb: 0.75,
                  fontSize: '0.7rem',
               }}
            >
               Photo Preview
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 1, fontSize: '0.8rem' }}>
               Photos will appear here as photographers upload them
            </Typography>
            <Box
               sx={{
                  height: ROW_HEIGHT,
                  borderRadius: 1,
                  backgroundColor: theme.palette.action.hover,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  border: `1px dashed ${theme.palette.divider}`,
               }}
            >
               <PhotoCameraOutlinedIcon sx={{ fontSize: 48, color: theme.palette.text.disabled }} />
            </Box>
         </Box>
      );
   }

   return (
      <Box sx={{ mb: 1.5 }}>
         <Typography
            variant="caption"
            sx={{
               fontWeight: 600,
               color: '#64748b',
               display: 'block',
               mb: 0.75,
               fontSize: '0.7rem',
            }}
         >
            Photo Preview
         </Typography>

         <Box
            sx={{ position: 'relative' }}
            onMouseEnter={() => setHoverStrip(true)}
            onMouseLeave={() => {
               setHoverStrip(false);
               setOverlayPhotoId(null);
            }}
         >
            {hoverStrip && photos.length > 0 && (
               <>
                  <IconButton
                     size="small"
                     onClick={() => scrollByAmount(-1)}
                     sx={{
                        position: 'absolute',
                        left: 4,
                        top: '50%',
                        transform: 'translateY(-50%)',
                        zIndex: 3,
                        bgcolor: 'rgba(0,0,0,0.45)',
                        color: '#fff',
                        '&:hover': { bgcolor: 'rgba(0,0,0,0.65)' },
                        display: { xs: 'none', sm: 'flex' },
                     }}
                     aria-label="Scroll left"
                  >
                     <ChevronLeftIcon />
                  </IconButton>
                  <IconButton
                     size="small"
                     onClick={() => scrollByAmount(1)}
                     sx={{
                        position: 'absolute',
                        right: 4,
                        top: '50%',
                        transform: 'translateY(-50%)',
                        zIndex: 3,
                        bgcolor: 'rgba(0,0,0,0.45)',
                        color: '#fff',
                        '&:hover': { bgcolor: 'rgba(0,0,0,0.65)' },
                        display: { xs: 'none', sm: 'flex' },
                     }}
                     aria-label="Scroll right"
                  >
                     <ChevronRightIcon />
                  </IconButton>
               </>
            )}

            <Box
               ref={scrollRef}
               className="inline-photo-scroller-strip"
               onMouseDown={(e) => {
                  if (e.button !== 0) return;
                  const el = scrollRef.current;
                  if (!el) return;
                  dragRef.current = {
                     active: true,
                     startX: e.pageX,
                     scrollLeft: el.scrollLeft,
                  };
               }}
               sx={{
                  display: 'flex',
                  flexDirection: 'row',
                  flexWrap: 'nowrap',
                  gap: 1,
                  overflowX: 'auto',
                  overflowY: 'hidden',
                  height: ROW_HEIGHT,
                  touchAction: 'pan-x',
                  cursor: 'grab',
                  scrollbarWidth: 'thin',
                  pb: 0.5,
                  '&:active': { cursor: 'grabbing' },
                  '&::-webkit-scrollbar': { height: 6 },
                  '&::-webkit-scrollbar-thumb': {
                     backgroundColor: theme.palette.action.disabled,
                     borderRadius: 3,
                  },
               }}
            >
               {loadingInitial &&
                  Array.from({ length: SKELETON_COUNT }).map((_, i) => (
                     <Skeleton
                        key={`sk-${i}`}
                        variant="rectangular"
                        animation="wave"
                        sx={{
                           height: ROW_HEIGHT,
                           width: 140,
                           flexShrink: 0,
                           borderRadius: 1,
                        }}
                     />
                  ))}

               {!loadingInitial &&
                  photos.map((photo, index) => {
                     const imageSrc = photo.thumbnailUrl || photo.url || '';
                     const videoSrc = (photo.videoUrl || photo.url || '').trim();
                     const isVideo =
                        photo.mediaType === 'video' || feedPhotoLooksLikeVideo(photo);
                     const posterImage = albumVideoImagePosterUrl({
                        thumbnailUrl: photo.thumbnailUrl,
                        url: photo.url,
                        videoUrl: photo.videoUrl,
                     });
                     const idleVideoSrc = albumVideoSrcWithPeekHint(videoSrc);
                     const showOverlay = overlayPhotoId === photo.id;
                     const indexOneBased = index + 1;
                     const isImageLoaded = loadedIds.has(photo.id);
                     const isPlaying = isVideo && playingVideoId === photo.id;
                     const markMediaLoaded = () =>
                        setLoadedIds((prev) => new Set(prev).add(photo.id));

                     const mediaSx = {
                        height: ROW_HEIGHT,
                        width: 'auto',
                        maxWidth: 'min(90vw, 480px)',
                        objectFit: 'contain' as const,
                        display: 'block',
                        opacity: isImageLoaded ? 1 : 0,
                        transition: 'opacity 0.35s ease',
                        userSelect: 'none' as const,
                        pointerEvents: 'auto' as const,
                     };

                     return (
                        <Box
                           key={photo.id}
                           data-photo-tile="1"
                           sx={{
                              position: 'relative',
                              height: ROW_HEIGHT,
                              flexShrink: 0,
                              borderRadius: 1,
                              overflow: 'hidden',
                              backgroundColor: theme.palette.action.hover,
                           }}
                           onMouseEnter={() => setOverlayPhotoId(photo.id)}
                           onMouseLeave={() => setOverlayPhotoId((id) => (id === photo.id ? null : id))}
                           onTouchStart={() => startLongPress(photo.id)}
                           onTouchEnd={() => {
                              endLongPress();
                           }}
                           onTouchCancel={endLongPress}
                        >
                           {isPlaying ? (
                              <Box
                                 component="video"
                                 ref={videoRef}
                                 src={videoSrc}
                                 {...(posterImage ? { poster: posterImage } : {})}
                                 controls
                                 autoPlay
                                 onEnded={() => setPlayingVideoId(null)}
                                 onClick={(e: React.MouseEvent) => e.stopPropagation()}
                                 sx={{
                                    height: ROW_HEIGHT,
                                    width: 'auto',
                                    minWidth: 200,
                                    maxWidth: 'min(90vw, 480px)',
                                    objectFit: 'contain',
                                    display: 'block',
                                    outline: 'none',
                                 }}
                              />
                           ) : isVideo && videoSrc ? (
                              <Box
                                 component="video"
                                 src={idleVideoSrc}
                                 {...(posterImage ? { poster: posterImage } : {})}
                                 muted
                                 playsInline
                                 preload="metadata"
                                 onLoadedData={markMediaLoaded}
                                 onDragStart={(e) => e.preventDefault()}
                                 sx={mediaSx}
                              />
                           ) : imageSrc ? (
                              <Box
                                 component="img"
                                 src={imageSrc}
                                 alt={photo.title || ''}
                                 loading="lazy"
                                 draggable={false}
                                 onLoad={markMediaLoaded}
                                 onDragStart={(e) => e.preventDefault()}
                                 sx={mediaSx}
                              />
                           ) : (
                              <Box
                                 sx={{
                                    height: ROW_HEIGHT,
                                    width: 120,
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                 }}
                              >
                                 <PhotoCameraOutlinedIcon color="disabled" />
                              </Box>
                           )}

                           {/* Video play button overlay (shown when not playing) */}
                           {isVideo && !isPlaying && (
                              <Box
                                 onClick={(e) => {
                                    e.stopPropagation();
                                    if (videoRef.current) {
                                       videoRef.current.pause();
                                    }
                                    setPlayingVideoId(photo.id);
                                 }}
                                 sx={{
                                    position: 'absolute',
                                    inset: 0,
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    cursor: 'pointer',
                                    bgcolor: 'rgba(0,0,0,0.25)',
                                    transition: 'background-color 0.2s',
                                    '&:hover': { bgcolor: 'rgba(0,0,0,0.4)' },
                                 }}
                              >
                                 <PlayCircleOutlineIcon
                                    sx={{
                                       fontSize: 56,
                                       color: '#fff',
                                       filter: 'drop-shadow(0 2px 4px rgba(0,0,0,0.5))',
                                    }}
                                 />
                              </Box>
                           )}

                           {showOverlay && !isPlaying && (
                              <Box
                                 onClick={() => setOverlayPhotoId(null)}
                                 sx={{
                                    position: 'absolute',
                                    top: 0,
                                    right: 0,
                                    p: 0.5,
                                    zIndex: 2,
                                 }}
                              >
                                 <IconButton
                                    size="small"
                                    sx={{
                                       color: '#fff',
                                       bgcolor: 'rgba(0,0,0,0.35)',
                                       '&:hover': { bgcolor: 'rgba(0,0,0,0.55)' },
                                    }}
                                    aria-label="Download"
                                    disabled={downloadingId === photo.id}
                                    onMouseDown={(e) => e.stopPropagation()}
                                    onClick={(e) => {
                                       e.stopPropagation();
                                       handleDownload(photo, indexOneBased);
                                    }}
                                 >
                                    <DownloadIcon fontSize="small" />
                                 </IconButton>
                              </Box>
                           )}
                        </Box>
                     );
                  })}

               {loadingMore && (
                  <Box
                     sx={{
                        display: 'flex',
                        gap: 1,
                        flexShrink: 0,
                        alignItems: 'center',
                        pl: 1,
                     }}
                  >
                     <Skeleton variant="rectangular" width={100} height={ROW_HEIGHT - 20} sx={{ borderRadius: 1 }} />
                  </Box>
               )}

               <Box ref={sentinelRef} sx={{ width: 8, flexShrink: 0, height: 1 }} aria-hidden />
            </Box>

            {!loadingInitial && totalCount > 0 && (
               <Typography
                  variant="caption"
                  sx={{
                     position: 'absolute',
                     right: 8,
                     bottom: -2,
                     fontSize: '0.65rem',
                     color: theme.palette.text.secondary,
                     bgcolor: theme.palette.mode === 'dark' ? 'rgba(0,0,0,0.5)' : 'rgba(255,255,255,0.85)',
                     px: 0.75,
                     py: 0.25,
                     borderRadius: 0.5,
                     pointerEvents: 'none',
                  }}
               >
                  {counterLabel}
               </Typography>
            )}
         </Box>
      </Box>
   );
};

export default InlinePhotoScroller;
